import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SharedHeader from './SharedHeader';
import { AllCustomerShopping, createCustomerTransaction, updateCustomerTransaction } from '../../services/api';
import { 
  Search, Filter, Calendar, Download, Eye, 
  TrendingUp, DollarSign, ShoppingCart, Users,
  ArrowUpRight, ArrowDownRight, X, Receipt
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const SalesHistoryPage = ({ onBack, onMenuAction, onAddTransaction, refreshKey }) => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  useEffect(() => {
    fetchSalesData();
  }, [currentUser, refreshKey]);

  const fetchSalesData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const response = await AllCustomerShopping({});
      const data = response?.extractedData || response?.data || response || [];
      // Exclude OTP placeholder transactions from view
      const visibleData = (Array.isArray(data) ? data : []).filter(
        (item) => String(item?.status || '').toLowerCase() !== 'otp_sent'
      );
      
      // Transform API data to sales history format
      const transformedData = visibleData.map(item => ({
        id: item.id || item._id,
        customer: item.name || item.email_id?.split('@')[0] || 'Customer',
        email: item.email_id,
        amount: parseFloat(item.amount || item.amount_redeem_value || 0),
        items: `${Math.floor(Math.random() * 5) + 1} items`,
        date: item.transaction_date || item.created_on,
        status: item.status || 'Completed',
        paymentMethod: item.payment_method || ['Credit Card', 'UPI', 'Cash', 'Debit Card'][Math.floor(Math.random() * 4)],
        points: item.point_given || item.credited_points|| 0,
        mobile: item.mobile,
        transactionId: item.transaction_id || `TXN${Date.now()}`,
        redemptionPoints: item.point_balance || item.redeem_points || 0,
      }));
      
      setSalesData(transformedData);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      toast.error('Failed to load sales history');
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const calculateSummaryStats = () => {
    const filteredData = getFilteredData();
    const totalSales = filteredData.reduce((sum, item) => sum + item.amount, 0);
    const totalTransactions = filteredData.length;
    const avgOrderValue = totalTransactions > 0 ? totalSales / totalTransactions : 0;
    const totalPoints = filteredData.reduce((sum, item) => sum + item.points, 0);

    return {
      totalSales,
      totalTransactions,
      avgOrderValue,
      totalPoints
    };
  };
  const businessConfig = {
    businessName: currentUser?.displayName || 'My Business',
    businessEmail: currentUser?.email || 'business@example.com',
    businessPhone: '+91 98765 43210',
    businessAddress: '123 Business Street, City, State 12345',
    fixedDiscount: 10,
    pointsPerRupee: 0.1,
    minRedemptionPoints: 100
  };

  const getFilteredData = () => {
    let filtered = [...salesData];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.transactionId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date filter
    if (dateFilter === 'today') {
      const today = new Date().toDateString();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date).toDateString();
        return itemDate === today;
      });
    } else if (dateFilter === 'thisWeek') {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      filtered = filtered.filter(item => new Date(item.date) >= oneWeekAgo);
    } else if (dateFilter === 'thisMonth') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      filtered = filtered.filter(item => new Date(item.date) >= startOfMonth);
    } else if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
      const startDate = new Date(customDateRange.start);
      const endDate = new Date(customDateRange.end);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    return filtered;
  };

  const getSortedData = (data) => {
    return [...data].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const closeTransactionModal = () => {
    setSelectedTransaction(null);
    setShowTransactionModal(false);
  };

  const filteredData = getFilteredData();
  const sortedData = getSortedData(filteredData);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const summaryStats = calculateSummaryStats();

  const submitRedemption = async (txn) => {
    try {
      if (!txn) return;
      const pts = Number(txn.redemptionPoints || 0);
      if (!pts || pts <= 0) return;
      setSubmittingId(txn.id);
      // 1) Create negative points transaction (deduction)
      await createCustomerTransaction({
        email_id: txn.email,
        name: txn.customer,
        amount: 0,
        point_given: -pts,
        transaction_date: new Date().toISOString(),
        payment_method: 'Redeem',
        status: 'Completed',
      });
      // 2) Mark the pending redemption record as completed
      await updateCustomerTransaction(txn.id, { status: 'completed', completedDate: new Date().toISOString() });
      toast.success('Redemption submitted and points deducted');
      await fetchSalesData();
    } catch (e) {
      console.error('Submit redemption failed', e);
      toast.error('Failed to submit redemption');
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SharedHeader 
          title="Sales History"
          subtitle="Transaction History & Analytics"
          showBackButton={true}
          onBack={onBack}
          onMenuAction={onMenuAction}
        />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading sales history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
     
       <SharedHeader 
                          title={businessConfig.businessName}
                          subtitle="Business Dashboard"
                          onMenuAction={onMenuAction}
                        />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Stats */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Sales</p>
                <p className="text-2xl font-bold">₹{summaryStats.totalSales.toLocaleString()}</p>
              </div>
              <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Transactions</p>
                <p className="text-2xl font-bold">{summaryStats.totalTransactions}</p>
              </div>
              <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                <ShoppingCart className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Avg Order Value</p>
                <p className="text-2xl font-bold">₹{Math.round(summaryStats.avgOrderValue)}</p>
              </div>
              <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Points Given</p>
                <p className="text-2xl font-bold">{summaryStats.totalPoints}</p>
              </div>
              <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div> */}

<div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="text-indigo-600 hover:underline font-medium"
          >
            ← Back
          </button>
        </div>
        {/* Filter Section with Headings */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sales History </h3>
            <p className="text-sm text-gray-600">Filter transactions by date range and search criteria</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 mt-3" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today's Transactions</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => onAddTransaction && onAddTransaction()}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>Add </span>
                </button>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors">
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>

          {dateFilter === 'custom' && (
            <div className="flex gap-4 mt-4">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>

        {/* Sales Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Sales Transactions</h2>
          </div>

          {paginatedData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="text-left py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {sortBy === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('amount')}
                    >
                      Amount {sortBy === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    {/* <th className="text-right py-3 px-6 font-semibold text-gray-700">Items</th> */}
                    <th 
                      className="text-right py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('points')}
                    >
                      Points Given{sortBy === 'points' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('redemptionPoints')}
                    >
                      Point Redeemed By Customer{sortBy === 'redemptionPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortBy === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-700">Payment</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-6 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedData.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-semibold text-gray-900">{transaction.customer}</p>
                          <p className="text-sm text-gray-500">{transaction.email}</p>
                          <p className="text-sm text-gray-500">{transaction.mobile}</p>
                        </div>
                      </td>
                       <td className="text-right py-3 px-4 text-purple-600 font-semibold">
  ₹{parseFloat(transaction.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
</td>
                      {/* <td className="py-4 px-6 text-gray-700">{transaction.items}</td> */}
                      <td className="py-4 px-6 text-right">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {transaction.points} pts
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                          {transaction.redemptionPoints} pts 
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-500">
                        {new Date(transaction.date).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-6 text-gray-700">{transaction.paymentMethod}</td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          String(transaction.status || '').toLowerCase() === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : String(transaction.status || '').toLowerCase() === 'approved'
                            ? 'bg-blue-100 text-blue-800'
                            : String(transaction.status || '').toLowerCase() === 'pending' || String(transaction.status || '').toLowerCase() === 'pending_customer'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleViewTransaction(transaction)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {String(transaction.paymentMethod || '').toLowerCase() === 'redeem' && String(transaction.status || '').toLowerCase() === 'approved' && (transaction.redemptionPoints || 0) > 0 && (
                            <button
                              onClick={() => submitRedemption(transaction)}
                              disabled={submittingId === transaction.id}
                              className={`px-3 py-1 rounded text-white text-xs ${submittingId === transaction.id ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                            >
                              {submittingId === transaction.id ? 'Submitting...' : 'Submit'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
              <p className="text-gray-600">
                {searchTerm || dateFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'No sales transactions available'
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-6 py-4">
              <p className="text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} transactions
              </p>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === i + 1 
                        ? 'bg-blue-600 text-white' 
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Transaction Details Modal */}
        {showTransactionModal && selectedTransaction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Transaction Details</h3>
                <button
                  onClick={closeTransactionModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{selectedTransaction.customer}</h4>
                    <p className="text-sm text-gray-500">Transaction ID: {selectedTransaction.id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    {/* <p className="font-semibold text-lg">₹{selectedTransaction.amount?.toLocaleString()}</p> */}
                     <p className="font-semibold text-lg">
  ₹{parseFloat(selectedTransaction.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Points Given</p>
                    <p className="font-semibold text-lg text-blue-600">{selectedTransaction.points} pts</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Redemption Points</p>
                    <p className="font-semibold text-lg text-blue-600">{selectedTransaction.redemptionPoints} pts</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Customer Email</span>
                      <span className="font-medium">{selectedTransaction.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Mobile</span>
                      <span className="font-medium">{selectedTransaction.mobile}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date</span>
                      <span className="font-medium">{new Date(selectedTransaction.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Time</span>
                      <span className="font-medium">{new Date(selectedTransaction.date).toLocaleTimeString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Method</span>
                      <span className="font-medium">{selectedTransaction.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedTransaction.status === 'Completed' 
                          ? 'bg-green-100 text-green-800' 
                          : selectedTransaction.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedTransaction.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesHistoryPage;
